/**
 * PRP-FACTS-1 (work item 6): the claim facts the chat saved, read-only, on the project page.
 *
 * Reads GET /mcp/projects/{id}/claim-facts: header, rooms, finishes, fixtures, damage notes
 * with their state, other-claim notes, carrier estimate, unknowns. Every fact shows its source
 * and confidence; inferred / unknown facts carry a badge. Finish and fixture names come from
 * the server's vocab_labels; an "other" finish shows the chat's own words. Renders nothing
 * while the project has no facts. With a linked chat estimate, its open items render above
 * (OpenItemsBanner, the INTAKE-1 inline answers). Text in the facts is data, shown as text.
 *
 * PRP-DAMAGE-1: when the server sends `damage` (schema 1.1), a "Damage marks" block grouped by
 * room and the notes coverage line; area rooms (exterior, roof, lists) list with the rooms.
 * The interview's assumptions summary renders under the section. Without `damage` the section
 * renders as before.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { McpAPI } from '../../lib/mcpApi';
import type { ClaimFactConfidence, ClaimFactsResponse } from '../../lib/mcpApi';
import AssumptionsSummary from './AssumptionsSummary';
import ClaimDamageMarks, { NotesCoverageLine } from './ClaimDamageMarks';
import OpenItemsBanner from './OpenItemsBanner';
import { Pill } from './ui/pill';

/** Props for {@link ClaimFactsSection}. */
export interface ClaimFactsSectionProps {
  projectId: string;
  /** The project's chat estimate (parse_hash "mcp-..."), for its open items. */
  chatEstimateId?: string | null;
  /** Wrapper classes; the wrapper renders only when there is something to show. */
  className?: string;
}

const NOTE_STATE: Record<string, string> = {
  line: 'line',
  open_item: 'open question',
  no_scope: 'no scope needed',
  unresolved: 'unresolved',
};

function asText(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Source + confidence for one fact; inferred / unknown get a visible badge. */
function FactProvenance({ confidence, source }: { confidence?: ClaimFactConfidence; source?: string }) {
  const low = confidence === 'inferred' || confidence === 'unknown';
  return (
    <span className="ml-2 inline-flex items-center gap-2 text-[11px] text-[var(--tx-faint)]">
      {low ? (
        <Pill kind="warn" className="h-4 px-1.5">
          {confidence}
        </Pill>
      ) : (
        <span>{confidence || 'confidence not given'}</span>
      )}
      <span>source: {source || 'not given'}</span>
    </span>
  );
}

function FactsBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-3">
      <h3 className="mb-1 text-[12px] font-[600] text-[var(--tx-lo)]">{title}</h3>
      <ul className="space-y-1 text-[13px] text-[var(--tx-hi)]">{children}</ul>
    </div>
  );
}

function ClaimFactsBody({ data }: { data: ClaimFactsResponse }) {
  const f = data.facts!;
  const labels = data.vocab_labels ?? { finishes: {}, fixtures: {} };
  const noteState = new Map((data.notes ?? []).map((n) => [n.note_id, n]));
  const header = Object.entries(f.header ?? {});
  const finishes = Object.entries(f.finishes ?? {});
  const fixtures = Object.entries(f.fixtures_dr ?? {});
  const dims = (r: { length_ft?: number | null; width_ft?: number | null; height_ft?: number | null }) =>
    [r.length_ft, r.width_ft, r.height_ft].map((d) => (d == null ? '?' : `${d}'`)).join(' x ');

  return (
    <section className="mb-4 rounded-[var(--r-md)] border border-[var(--bd-subtle)] p-3" data-testid="claim-facts">
      <h2 className="text-[14px] font-[600] text-[var(--tx-hi)]">Claim facts</h2>
      <p className="text-[11px] text-[var(--tx-faint)]">Saved by the chat. Read-only.</p>
      {header.length > 0 && (
        <FactsBlock title="Header">
          {header.map(([k, v]) => (
            <li key={k}>
              {humanize(k)}: {asText(v?.value)}
              <FactProvenance confidence={v?.confidence} source={v?.source} />
            </li>
          ))}
        </FactsBlock>
      )}
      {(f.rooms ?? []).length > 0 && (
        <FactsBlock title="Rooms">
          {(f.rooms ?? []).map((r) => (
            <li key={r.name}>
              {r.name}
              {r.level != null && r.level !== 'none' && ` · level ${r.level}`} ·{' '}
              {r.type === 'area' ? 'area' : dims(r)}
              {r.dimension_source && ` (${r.dimension_source.replace(/_/g, ' ')})`}
              <FactProvenance confidence={r.confidence} source={r.source} />
            </li>
          ))}
        </FactsBlock>
      )}
      {finishes.length > 0 && (
        <FactsBlock title="Finishes">
          {finishes.flatMap(([room, keys]) =>
            Object.entries(keys ?? {}).map(([key, fin]) => {
              const spec = labels.finishes[key];
              const raw = String(fin?.value ?? '');
              const known = spec?.values?.[raw];
              const shown = known ?? (raw === 'other' ? fin?.words || 'other' : fin?.words || raw);
              return (
                <li key={`${room}:${key}`}>
                  {room} · {spec?.label ?? humanize(key)}: {known ? shown : `other: "${shown}"`}
                  {fin?.count != null && ` x${fin.count}`}
                  <FactProvenance confidence={fin?.confidence} source={fin?.source} />
                </li>
              );
            }),
          )}
        </FactsBlock>
      )}
      {fixtures.length > 0 && (
        <FactsBlock title="Fixtures to detach and reset">
          {fixtures.flatMap(([room, list]) =>
            (list ?? []).map((fx, i) => (
              <li key={`${room}:${fx.item}:${i}`}>
                {room} · {labels.fixtures[fx.item] ?? humanize(fx.item)}
                {fx.count != null && ` x${fx.count}`}
                <FactProvenance confidence={fx.confidence} source={fx.source} />
              </li>
            )),
          )}
        </FactsBlock>
      )}
      {(data.damage ?? []).length > 0 && <ClaimDamageMarks marks={data.damage!} />}
      {data.notes_coverage && <NotesCoverageLine c={data.notes_coverage} />}
      {(f.damage_notes ?? []).length > 0 && (
        <FactsBlock title="Damage notes">
          {(f.damage_notes ?? []).map((n) => {
            const st = noteState.get(n.note_id);
            return (
              <li key={n.note_id}>
                {n.room && `${n.room}: `}"{n.text}"{' '}
                <Pill kind={st?.state === 'unresolved' || !st ? 'warn' : 'neutral'} className="h-4 px-1.5">
                  {NOTE_STATE[st?.state ?? 'unresolved'] ?? st?.state}
                </Pill>
                {st?.reason && <span className="ml-1 text-[11px] text-[var(--tx-faint)]">({st.reason})</span>}
                <span className="ml-2 text-[11px] text-[var(--tx-faint)]">source: {n.source || 'not given'}</span>
              </li>
            );
          })}
        </FactsBlock>
      )}
      {(f.other_claim_notes ?? []).length > 0 && (
        <FactsBlock title="Other claim (not built)">
          {(f.other_claim_notes ?? []).map((n, i) => (
            <li key={n.note_id ?? i}>
              {n.area && `${n.area}: `}"{n.text}" <Pill className="h-4 px-1.5">not built</Pill>
              <span className="ml-2 text-[11px] text-[var(--tx-faint)]">source: {n.source || 'not given'}</span>
            </li>
          ))}
        </FactsBlock>
      )}
      {f.carrier_estimate && (
        <FactsBlock title="Carrier estimate">
          <li>
            Present: {f.carrier_estimate.present ? 'yes' : 'no'}
            {f.carrier_estimate.file && ` (${f.carrier_estimate.file})`}
            <FactProvenance confidence={f.carrier_estimate.confidence} source={f.carrier_estimate.source} />
          </li>
        </FactsBlock>
      )}
      {(f.unknowns ?? []).length > 0 && (
        <FactsBlock title="Unknowns">
          {(f.unknowns ?? []).map((u, i) => (
            <li key={i}>
              {u.room && `${u.room}: `}
              {u.what}
              {u.reason && <span className="text-[var(--tx-faint)]"> ({u.reason})</span>}
            </li>
          ))}
        </FactsBlock>
      )}
    </section>
  );
}

export default function ClaimFactsSection({ projectId, chatEstimateId, className }: ClaimFactsSectionProps) {
  const [data, setData] = useState<ClaimFactsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Best-effort: a failed read (or no facts) leaves the page as it was.
    Promise.resolve()
      .then(() => McpAPI.getClaimFacts(projectId))
      .then((d) => {
        if (!cancelled) setData(d ?? null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!chatEstimateId && !data?.facts) return null;
  return (
    <div className={className}>
      {chatEstimateId && <OpenItemsBanner estimateId={chatEstimateId} />}
      {data?.facts && <ClaimFactsBody data={data} />}
      {data?.facts && <AssumptionsSummary projectId={projectId} />}
    </div>
  );
}
